import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { useI18n } from "../i18n";
import { usePricing } from "./PricingContext";

const AuthContext = createContext(null);

const API = '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("avisocar_token"));
  // iter71 1.4 — true when the stored session could not be recovered and the
  // user must log in again (push subscription is left INTACT).
  const [sessionExpired, setSessionExpired] = useState(false);
  const { setAccountLanguage } = useI18n();
  const { country: pricingCountry, setCountry: setPricingCountry } = usePricing();

  // RC-WEB-5 GATE 14 — el idioma EXPLÍCITO de la cuenta (`users.language`) es la
  // fuente autoritativa y viaja a cualquier cliente. Sin idioma explícito se
  // mantiene el default regional por país.
  useEffect(() => {
    if (!user) return;
    setAccountLanguage(user.language || null, user.country_code);
  }, [user?.language, user?.country_code, setAccountLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  // CORTE 4-B 1.2 — REGLA DURA: logueado, el precio/método/QR de cobro se
  // resuelve por el PAÍS DE LA CUENTA, nunca por geo/default.
  useEffect(() => {
    const cc = (user?.country_code || "").toUpperCase().slice(0, 2);
    if (cc && cc !== pricingCountry) setPricingCountry(cc);
  }, [user?.country_code, pricingCountry, setPricingCountry]);

  const storeSession = (newToken, refreshToken) => {
    localStorage.setItem("avisocar_token", newToken);
    if (refreshToken) localStorage.setItem("avisocar_refresh", refreshToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
  };

  // iter71 1.2 — REGLA DURA: la expiración de sesión JAMÁS toca la push
  // subscription. Solo limpia el estado local de auth.
  const softLogout = () => {
    localStorage.removeItem("avisocar_token");
    localStorage.removeItem("avisocar_refresh");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setSessionExpired(true);
  };

  // iter71 1.1 — rolling refresh: cada apertura renueva la sesión 180 días.
  const tryRefresh = async () => {
    const refresh = localStorage.getItem("avisocar_refresh");
    if (!refresh) return false;
    try {
      const r = await axios.post(`${API}/auth/refresh`, { refresh_token: refresh });
      storeSession(r.data.token, r.data.refresh_token);
      setUser(r.data.user);
      setSessionExpired(false);
      return true;
    } catch (_) {
      try { localStorage.removeItem("avisocar_refresh"); } catch (_) { /* ignore */ }
      return false;
    }
  };

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      // No JWT — maybe a refresh token survives (app open after JWT expiry).
      (async () => {
        const ok = await tryRefresh();
        if (!ok && localStorage.getItem("avisocar_refresh") === null) {
          // nothing to recover
        }
        setLoading(false);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setSessionExpired(false);
      // Rolling renewal on every app open (fire-and-forget rotation).
      tryRefresh();
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        // JWT expired/revoked → try the rolling refresh token first.
        const recovered = await tryRefresh();
        if (!recovered) softLogout();
      }
      // Network errors: keep the session — NEVER log out on a transient failure.
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, refresh_token: newRefresh, user: userData } = response.data;

    storeSession(newToken, newRefresh);
    setUser(userData);
    setSessionExpired(false);

    // iter68 F2 — re-enable this device's push subscription (fire-and-forget).
    // Only applies to THIS device/browser with permission already granted.
    try {
      const { reenablePushOnLogin } = await import("../utils/pushLogin");
      reenablePushOnLogin();
    } catch (_) { /* best-effort */ }

    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { token: newToken, refresh_token: newRefresh, user: newUser } = response.data;

    storeSession(newToken, newRefresh);
    setUser(newUser);
    setSessionExpired(false);

    return newUser;
  };

  // iter71 1.3 — EXPLICIT logout is the ONLY flow allowed to disable push.
  // Owners get a confirmation modal warning that this phone will stop
  // receiving vehicle alerts and calls.
  const logout = async ({ skipConfirm = false } = {}) => {
    const isOwner = user?.role !== "admin";
    let pushGranted = false;
    try { pushGranted = typeof Notification !== "undefined" && Notification.permission === "granted"; } catch (_) { /* ignore */ }

    if (!skipConfirm && isOwner && pushGranted) {
      const ok = window.confirm(
        "Si cierras sesión, este teléfono DEJARÁ de recibir avisos y llamadas de tu vehículo.\n\n¿Continuar?"
      );
      if (!ok) return false;
    }

    // Disable this device's push subscription FIRST so that, when a different
    // user logs in on the same browser/device, push notifications for the
    // previous owner stop arriving here.
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          try {
            await axios.post(`${API}/push/unsubscribe-public`, {
              endpoint: sub.endpoint,
              reason: "logout_confirmed",
            });
          } catch (_) { /* best-effort */ }
          try { await sub.unsubscribe(); } catch (_) { /* best-effort */ }
        }
      }
    } catch (_) { /* best-effort */ }

    localStorage.removeItem("avisocar_token");
    localStorage.removeItem("avisocar_refresh");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setSessionExpired(false);
    return true;
  };

  // FIX 2: Add isAuthenticated for easier checks
  const isAuthenticated = !!user && !!token;

  // Refresh user data
  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        console.error("Error refreshing user:", error);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, token, isAuthenticated, refreshUser, sessionExpired }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
